import * as React from 'react'
import Box from '@mui/material/Box'
import { useAppDispatch, useAppSelector } from '../../core/redux/hooks'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { OffersListParams } from '../../offers/interfaces/offers'
import {
  FIRST_PAGE_CAMPAIGNS_TABLE,
  PER_PAGE_CAMPAIGNS_TABLE
} from '../../campaigns/constants'
import { PAGE, constants } from '../../commons/constants/constants'
import {
  setBackButtonRoute,
  setBackButtonTitle,
  setEntity
} from '../../ui/sidebar/slices'
import OffersList from '../../offers/OffersList'
import OffersListHeader from '../../offers/OffersListHeader'
import { getAgencyByID } from '../slices'
import { CircularProgress } from '@mui/material'
import { getValueForForm } from '../../commons/utils/utils'
import { RoutePath } from '../../core/routes/routePaths'
import { getOffers } from '../../offers/slices'

const AgencyOffers = (): React.ReactElement => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { offers, totalOffersList, tenantID } = useAppSelector((state) => ({
    offers: state.offers.offers,
    totalOffersList: state.offers.totalOffersList,
    tenantID: state.auth.tenantID
  }))

  const handleFormChange = ({
    agencyID,
    tenantID,
    search,
    categories,
    page,
    perPage
  }: OffersListParams): void => {
    Object.entries({
      agencyID,
      tenantID,
      search,
      categories,
      page,
      perPage
    }).forEach(([k, v]) => {
      const checkedValue = getValueForForm(k, v, searchParams.entries)
      setSearchParams((params) => {
        if (Array.isArray(checkedValue) && checkedValue.length === 0) {
          params.delete(k)
        } else if (checkedValue != null) {
          params.set(k, checkedValue as string)
          if (k !== PAGE) {
            params.set(PAGE, constants.MIN_PAGINATION_PER_PAGE.toString())
          }
        }
        return params
      })
    })
  }

  React.useEffect(() => {
    if (id != null) {
      void dispatch(getAgencyByID(id))
      dispatch(setEntity(constants.SIDEBAR_ENTITY_TYPES.AGENCY))
      dispatch(setBackButtonTitle(t`BackToAgencies`))
      dispatch(setBackButtonRoute(RoutePath.Agencies))
    }
    return () => {
      dispatch(setEntity(constants.SIDEBAR_ENTITY_TYPES.GENERAL))
    }
  }, [id, dispatch, t])

  React.useEffect(() => {
    if (id != null) {
      void dispatch(
        getOffers({
          categories: searchParams.get('categories')?.split(',') ?? [],
          tenantID,
          search: searchParams.get('search') ?? '',
          page: parseInt(
            searchParams.get('page') ?? FIRST_PAGE_CAMPAIGNS_TABLE.toString()
          ),
          perPage: parseInt(
            searchParams.get('perPage') ?? PER_PAGE_CAMPAIGNS_TABLE.toString()
          ),
          agencyID: id ?? searchParams.get('agencyID') ?? ''
        })
      )
    }
  }, [id, dispatch, searchParams, tenantID])

  return id != null && id !== ''
    ? (
      <Box className='flex-column'>
        <Box>
          <h1 className='title'>{t`Offers`}</h1>
        </Box>
        <OffersListHeader
          search={searchParams.get('search') ?? undefined}
          categories={searchParams.get('categories')?.split(',') ?? []}
          handleFormChange={handleFormChange}
        />
        <OffersList
          offersList={offers}
          total={totalOffersList}
          handleFormChange={handleFormChange}
          page={parseInt(
            searchParams.get('page') ?? FIRST_PAGE_CAMPAIGNS_TABLE.toString()
          )}
          perPage={parseInt(
            searchParams.get('perPage') ?? PER_PAGE_CAMPAIGNS_TABLE.toString()
          )}
        />
      </Box>
      )
    : (
      <CircularProgress />
      )
}

export default AgencyOffers
